import React from "react";
import { Button, Link } from "@heroui/react";
import { ArrowUpRight } from "lucide-react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import {
  workAgentCtas,
  workAgentDiagram,
  workAgentIntro,
  workAgentSteps,
} from "./work-agent";

const easeOut = [0.22, 1, 0.36, 1] as const;

const headerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.04 },
  },
};

const headerChild = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut },
  },
};

const stepWrap = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.06 },
  },
};

const stepChild = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease: easeOut },
  },
};

export default function WorkAgentSection() {
  return (
    <section
      id="work-agent"
      aria-labelledby="work-agent-heading"
      className="relative mx-auto w-full max-w-6xl scroll-mt-[calc(var(--navbar-height)+var(--intl-notice-height,0px)+12px)] px-6 py-20 sm:py-28 lg:px-8 lg:py-36"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-[38%] h-[420px] -translate-y-1/2 opacity-80"
        aria-hidden
      >
        <div
          className="mx-auto h-full max-w-3xl rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(0, 183, 255, 0.1), transparent 72%)",
          }}
        />
      </div>

      <LazyMotion features={domAnimation}>
        <m.div
          className="relative mb-10 flex flex-col items-center text-center sm:mb-12"
          variants={headerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10% 0px" }}
        >
          <m.p
            variants={headerChild}
            className="mb-3 flex items-center justify-center gap-2 text-[13px] font-medium tracking-wide text-[#00b7ff] sm:text-sm"
          >
            <span
              className="size-1.5 shrink-0 rounded-full bg-[#00b7ff] shadow-[0_0_8px_rgba(0,183,255,0.65)]"
              aria-hidden
            />
            {workAgentIntro.kicker}
          </m.p>
          <m.h2
            id="work-agent-heading"
            variants={headerChild}
            className="text-foreground max-w-[16ch] text-balance text-[1.65rem] font-bold leading-[1.25] tracking-tight sm:max-w-none sm:text-3xl sm:leading-[1.2] lg:text-[2rem]"
          >
            {workAgentIntro.title}
          </m.h2>
          <m.div
            variants={headerChild}
            className="text-default-500 mt-4 max-w-[54ch] text-pretty text-[15px] leading-relaxed sm:text-base"
          >
            {workAgentIntro.body.map((paragraph) => (
              <p key={paragraph} className="mt-2 first:mt-0">
                {paragraph}
              </p>
            ))}
          </m.div>
        </m.div>

        <m.figure
          className="relative mx-auto w-full"
          initial={{ opacity: 0, y: 28, scale: 0.985 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-8% 0px" }}
          transition={{ duration: 0.62, ease: easeOut, delay: 0.06 }}
        >
          <div
            className="pointer-events-none absolute left-1/2 top-[42%] h-[58%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-90 blur-3xl"
            aria-hidden
            style={{
              background:
                "radial-gradient(closest-side, rgba(0, 183, 255, 0.16), transparent 74%)",
            }}
          />
          <img
            src={workAgentDiagram.src}
            alt={workAgentDiagram.alt}
            width={1600}
            height={900}
            loading="lazy"
            decoding="async"
            className="relative z-10 mx-auto h-auto w-full max-w-[920px] select-none object-contain [mask-image:linear-gradient(to_bottom,transparent_0%,black_7%,black_90%,transparent_100%)]"
          />
        </m.figure>

        <div className="border-default-100/40 relative mt-2 overflow-hidden rounded-[22px] border bg-[#07070a] shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_32px_100px_rgba(0,0,0,0.45)] sm:mt-4">
          <div
            className="pointer-events-none absolute inset-0 rounded-[22px] opacity-[0.85]"
            aria-hidden
            style={{
              background: `
                linear-gradient(180deg, rgba(255,255,255,0.055) 0%, transparent 38%),
                linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)
              `,
              backgroundSize: "100% 100%, 22px 22px, 22px 22px",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/[0.04]"
            aria-hidden
          />

          <m.ol
            className="relative z-10 grid w-full min-w-0 grid-cols-1 lg:grid-cols-3"
            variants={stepWrap}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-8% 0px" }}
          >
            {workAgentSteps.map((step, index) => (
              <m.li
                key={step.index}
                variants={stepChild}
                className={`flex min-w-0 flex-col gap-3 border-white/[0.08] px-6 py-7 sm:px-7 sm:py-8 ${
                  index > 0 ? "border-t lg:border-t-0 lg:border-l" : ""
                }`}
              >
                <p className="text-foreground text-[15px] font-semibold tracking-tight sm:text-base">
                  <span className="text-primary font-mono text-[13px] font-medium tabular-nums tracking-wide sm:text-sm">
                    {step.index}
                  </span>
                  <span className="text-default-400 mx-1.5 font-normal" aria-hidden>
                    ｜
                  </span>
                  {step.title}
                </p>
                <p className="text-default-500 text-pretty text-sm leading-relaxed sm:text-[15px]">
                  {step.body}
                </p>
              </m.li>
            ))}
          </m.ol>
        </div>

        <m.div
          className="relative mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:mt-10 sm:flex-row sm:items-center sm:gap-4"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-6% 0px" }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.08 }}
        >
          {workAgentCtas.map((cta) => (
            <Button
              key={cta.id}
              as={Link}
              href={cta.href}
              isExternal
              radius="full"
              variant="bordered"
              className="group border-foreground/20 text-small text-foreground h-auto min-h-0 min-w-0 flex-1 basis-0 border-1 bg-foreground/10 px-4 py-6 leading-5 font-medium backdrop-blur-md transition-[background-color,color,box-shadow,transform] duration-300 ease-out hover:border-foreground/20 hover:bg-foreground/30 hover:text-default-foreground hover:shadow-md hover:shadow-black/25 data-[hover=true]:border-foreground/20 sm:h-12 sm:max-w-[280px] sm:flex-none sm:basis-auto sm:px-5 sm:py-[10px]"
              startContent={
                <img
                  src={cta.logoSrc}
                  alt=""
                  width={22}
                  height={22}
                  className="size-[22px] rounded-[6px] object-cover"
                  aria-hidden
                />
              }
              endContent={
                <ArrowUpRight
                  className="text-default-500 size-3.5 shrink-0 transition-[color,transform] duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-default-foreground"
                  strokeWidth={1.75}
                  aria-hidden
                />
              }
            >
              {cta.label}
            </Button>
          ))}
        </m.div>
      </LazyMotion>
    </section>
  );
}
